
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import { useStore } from '@/store';
import { showToastSuccess } from '@/helpers';
import { Me } from '@/composables/User';
import { takeShopifyLinkRequest, takeShopifySsoAssertion } from '@/composables/shopifySsoAssertion';
import {
  confirmShopifyLinkCode,
  getShopifyLinkInfo,
  linkShopifyShopWithPassword,
  loginWithShopifySso,
  sendShopifyLinkCode,
  ShopifyLinkSession,
} from '@/composables/shopifySso';

type ShopifySsoState = 'loading' | 'link_required' | 'not_provisioned' | 'error'
  | 'link_choose' | 'link_login' | 'link_account' | 'link_code' | 'link_expired';

// Login from the Shopify app: the assertion was taken from the URL fragment at startup (shopifySsoAssertion.ts).
// "Compte à relier": when an account already has the shop's email, client_auth answers with a link request (the old
// connector's link brings it in the fragment): the merchant logs in to that account to add the shop to it, or creates
// another account with an address confirmed by a code.
export default defineComponent({
  name: 'ShopifySso',
  components: {
    BaseTextInput,
    SpmButton,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const state = ref<ShopifySsoState>('loading');

    const link = ref<string | null>(null);
    const shop = ref('');
    const maskedEmail = ref('');
    const codeEmail = ref('');
    const email = ref('');
    const password = ref('');
    const code = ref('');
    const formError = ref('');
    const formInfo = ref('');
    const loginNotice = ref('');
    const newAccount = ref(false);
    const busy = ref(false);

    const goToLogin = () => router.push({ name: 'login' });
    // In a new tab: the request lives in this page only, and stays valid while the password is reset.
    const forgotPasswordHref = computed(() => router.resolve({ name: 'user.forgot-password' }).href);

    const title = computed(() => {
      if (state.value.startsWith('link_') && state.value !== 'link_required') {
        return t(`shopifySso.link.${state.value.slice('link_'.length)}.title`);
      }
      return t(`shopifySso.${state.value}.title`);
    });

    const intro = computed(() => {
      switch (state.value) {
        case 'link_choose':
          return t('shopifySso.link.choose.text', { email: maskedEmail.value, shop: shop.value });
        case 'link_login':
          return loginNotice.value || t('shopifySso.link.login.text', { email: maskedEmail.value, shop: shop.value });
        case 'link_account':
          return t('shopifySso.link.account.text', { shop: shop.value });
        case 'link_code':
          return t('shopifySso.link.code.text', { email: codeEmail.value });
        case 'link_expired':
          return t('shopifySso.link.expired.text');
        default:
          return t(`shopifySso.${state.value}.text`);
      }
    });

    const submitLabel = computed(() => {
      if (state.value === 'link_login') return t('shopifySso.link.login.submit');
      if (state.value === 'link_account') return t('shopifySso.link.account.submit');
      return t('shopifySso.link.code.submit');
    });

    // The link page, once client_auth confirmed the request is still valid.
    const showLinkChoice = async (id: string) => {
      const info = await getShopifyLinkInfo(id);
      if (info.status !== 'ok') {
        state.value = info.status === 'expired' ? 'link_expired' : 'link_required';
        return;
      }
      link.value = id;
      shop.value = info.shop;
      maskedEmail.value = info.maskedEmail;
      newAccount.value = info.newAccount;
      state.value = 'link_choose';
    };

    const openLogin = (prefill: string, notice = '') => {
      formError.value = '';
      formInfo.value = '';
      password.value = '';
      email.value = prefill;
      loginNotice.value = notice;
      state.value = 'link_login';
    };

    const openAccount = () => {
      formError.value = '';
      formInfo.value = '';
      email.value = '';
      state.value = 'link_account';
    };

    const backToChoice = () => {
      formError.value = '';
      formInfo.value = '';
      state.value = 'link_choose';
    };

    const attemptsError = (key: string, attemptsLeft: number | null) => (attemptsLeft !== null
      ? `${t(key)} ${t('shopifySso.link.attemptsLeft', { count: attemptsLeft })}`
      : t(key));

    // The session is open on the new shop: same end as the Shopify login.
    const finish = async (idShop: number, toastKey: string) => {
      await Me(true, idShop);
      await showToastSuccess(t(toastKey));
      await router.replace({ name: 'home' });
    };

    const handleSession = async (result: ShopifyLinkSession, toastKey: string, address: string) => {
      switch (result.status) {
        case 'success':
          await finish(result.idShop, toastKey);
          return;
        case 'bad_credentials':
          formError.value = attemptsError('shopifySso.link.login.badCredentials', result.attemptsLeft);
          return;
        case 'code_invalid':
          formError.value = attemptsError('shopifySso.link.code.invalid', result.attemptsLeft);
          return;
        case 'email_taken':
          openLogin(address, t('shopifySso.link.account.taken'));
          return;
        case 'shop_taken':
          formError.value = t('shopifySso.link.shopTaken');
          return;
        case 'expired':
          state.value = 'link_expired';
          return;
        default:
          formError.value = t('shopifySso.link.failed');
      }
    };

    // `again`: a resend from the code page, which says that only the new code works.
    const requestCode = async (address: string, again = false) => {
      const sent = await sendShopifyLinkCode(link.value as string, address);
      switch (sent.status) {
        case 'code_sent':
          codeEmail.value = sent.maskedEmail;
          code.value = '';
          formError.value = '';
          formInfo.value = again ? t('shopifySso.link.code.resent', { email: sent.maskedEmail }) : '';
          state.value = 'link_code';
          return;
        case 'email_taken':
          openLogin(address, t('shopifySso.link.account.taken'));
          return;
        case 'expired':
          state.value = 'link_expired';
          return;
        default:
          formError.value = t(`shopifySso.link.account.${sent.status}`);
      }
    };

    const submit = async () => {
      if (busy.value || !link.value) return;
      formError.value = '';
      formInfo.value = '';
      busy.value = true;
      store.commit('general/showTheSpinner');
      try {
        if (state.value === 'link_login') {
          if (!email.value.trim() || !password.value) {
            formError.value = t('shopifySso.link.login.missing');
            return;
          }
          await handleSession(
            await linkShopifyShopWithPassword(link.value, email.value.trim(), password.value),
            'shopifySso.link.linked',
            email.value.trim(),
          );
        } else if (state.value === 'link_account') {
          if (!email.value.trim()) {
            formError.value = t('shopifySso.link.account.email_invalid');
            return;
          }
          await requestCode(email.value.trim());
        } else if (state.value === 'link_code') {
          // Spaces or dashes copied from the mail are not part of the code.
          const typedCode = code.value.replace(/\D/g, '');
          if (typedCode.length !== 6) {
            formError.value = t('shopifySso.link.code.invalid');
            return;
          }
          await handleSession(await confirmShopifyLinkCode(link.value, typedCode), 'shopifySso.link.created', email.value.trim());
        }
      } catch (e) {
        formError.value = t('shopifySso.link.failed');
      } finally {
        busy.value = false;
        store.commit('general/hideTheSpinner');
      }
    };

    const resendCode = async () => {
      if (busy.value || !link.value) return;
      busy.value = true;
      try {
        await requestCode(email.value.trim(), true);
      } catch (e) {
        formError.value = t('shopifySso.link.failed');
      } finally {
        busy.value = false;
      }
    };

    onMounted(async () => {
      const assertion = takeShopifySsoAssertion();
      if (!assertion) {
        const linkId = takeShopifyLinkRequest();
        if (linkId) {
          await showLinkChoice(linkId);
          return;
        }
        // The old Shopify link (transition) reports this case with a query parameter.
        state.value = route.query.error === 'shopify_link_required' ? 'link_required' : 'error';
        return;
      }

      store.commit('general/showTheSpinner');
      try {
        const result = await loginWithShopifySso(assertion);
        if (result.status === 'success') {
          await Me(true, result.idShop);
          await showToastSuccess(t('login_success'));
          await router.replace({ name: 'home' });
          return;
        }
        if (result.status === 'link_required' && result.link) {
          await showLinkChoice(result.link);
          return;
        }
        state.value = result.status;
      } catch (e) {
        state.value = 'error';
      } finally {
        store.commit('general/hideTheSpinner');
      }
    });

    return {
      t,
      state,
      title,
      intro,
      email,
      password,
      code,
      formError,
      formInfo,
      newAccount,
      busy,
      submitLabel,
      forgotPasswordHref,
      goToLogin,
      openLogin,
      openAccount,
      backToChoice,
      submit,
      resendCode,
    };
  },
});
